import api from './api';
import type { CompanyStatusFilter } from './api/companyApi';

export interface StatisticsOverview {
  year: number;
  aggregated: {
    totalTransactions: number;
    totalGeolocations: number;
    totalUniqueShipments: number;
    totalErroredShipments: number;
  };
  companies: Array<{
    company_id: number;
    company_name: string;
    licence_type: string;
    total_transactions: number;
    total_geolocations: number;
    total_unique_shipments: number;
    shipment_limit: number | null;
    geo_limit: number | null;
    partner_name: string | null;
    is_demo: boolean;
    disabled: boolean;
    license_start: string | null;
    license_end: string | null;
    analytics_enabled: boolean;
  }>;
  monthly: Array<{
    year: number;
    month: number;
    month_name: string;
    companies: Array<{
      company_id: number;
      company_name: string;
      licence_type: string;
      total_transactions: number;
      total_geolocations: number;
      total_unique_shipments: number;
      ytd_transactions: number;
      ytd_geolocations: number;
      ytd_unique_shipments: number;
      transactions_left: number;
      geolocations_left: number;
      percent_transactions_left: number;
      percent_geolocations_left: number;
      shipment_limit: number | null;
      geo_limit: number | null;
      partner_name: string | null;
      analytics_enabled: boolean;
    }>;
  }>;
  daily?: Array<{
    day: string;
    year: number;
    month: number;
    total_transactions: number;
    total_geolocations: number;
    total_unique_shipments: number;
  }>;
}

export interface CompanyStatistics {
  company: {
    id: number;
    name: string;
    licence_type: string;
    shipment_limit: number | null;
    geo_limit: number | null;
    partner_name: string | null;
    is_demo: boolean;
    disabled: boolean;
    license_start: string | null;
    license_end: string | null;
    analytics_enabled: boolean;
  };
  year: number;
  fromDate: string | null;
  toDate: string | null;
  availableYears: number[];
  yearly: {
    total_transactions: number;
    total_geolocations: number;
    total_unique_shipments: number;
    total_errored_shipments: number;
  };
  monthly: Array<{
    year: number;
    month: number;
    month_name: string;
    total_transactions: number;
    total_geolocations: number;
    total_unique_shipments: number;
    ytd_transactions: number;
    ytd_geolocations: number;
    ytd_unique_shipments: number;
    transactions_left: number;
    geolocations_left: number;
    percent_transactions_left: number;
    percent_geolocations_left: number;
  }>;
  daily: Array<{
    day: string;
    year: number;
    month: number;
    total_transactions: number;
    total_geolocations: number;
    total_unique_shipments: number;
  }>;
}

/**
 * Licence filter types:
 * - CL (Current): Active, not demo, licence not expired
 * - DL (Demo): Demo licences
 * - FL (Former): Expired licences, not demo
 * - AL (All): No filter
 */
export type LicenceFilterType = 'CL' | 'DL' | 'FL' | 'AL';

/**
 * Get statistics overview for all companies (Master Admin only)
 */
export const getStatisticsOverview = async (options?: {
  year?: number;
  fromDate?: string;
  toDate?: string;
  licenceFilter?: LicenceFilterType;
  statusFilter?: CompanyStatusFilter;
  includeDaily?: boolean;
}): Promise<StatisticsOverview> => {
  const params: any = {};
  if (options?.year) params.year = options.year;
  if (options?.fromDate) params.fromDate = options.fromDate;
  if (options?.toDate) params.toDate = options.toDate;
  if (options?.statusFilter) params.statusFilter = options.statusFilter;
  else if (options?.licenceFilter) params.licenceFilter = options.licenceFilter;
  if (options?.includeDaily) params.includeDaily = 'true';

  const response = await api.get('/statistics/overview', { params });
  return response.data.data;
};

/**
 * Get statistics for a specific company
 */
export const getCompanyStatistics = async (
  companyId: number,
  options?: {
    year?: number;
    fromDate?: string;
    toDate?: string;
  }
): Promise<CompanyStatistics> => {
  const params: any = {};
  if (options?.year) params.year = options.year;
  if (options?.fromDate) params.fromDate = options.fromDate;
  if (options?.toDate) params.toDate = options.toDate;

  const response = await api.get(`/statistics/company/${companyId}`, { params });
  return response.data.data;
};

/**
 * Get list of available years with statistics data
 */
export const getAvailableYears = async (): Promise<number[]> => {
  const response = await api.get('/statistics/years');
  return response.data.data.years;
};

/**
 * Shared helper: export statistics overview to a file (Excel or PDF).
 * Returns either a download URL or triggers direct download.
 */
const exportStatisticsToFile = async (
  endpoint: string,
  fileExtension: string,
  options?: {
    year?: number;
    fromDate?: string;
    toDate?: string;
    licenceFilter?: LicenceFilterType;
    statusFilter?: CompanyStatusFilter;
    companyId?: number;
  }
): Promise<string | null> => {
  const params: any = {};
  if (options?.year) params.year = options.year;
  if (options?.fromDate) params.fromDate = options.fromDate;
  if (options?.toDate) params.toDate = options.toDate;
  if (options?.statusFilter) params.statusFilter = options.statusFilter;
  else if (options?.licenceFilter) params.licenceFilter = options.licenceFilter;
  if (options?.companyId) params.companyId = options.companyId;

  const response = await api.get(endpoint, {
    params,
    responseType: 'blob'
  });

  // Check if response is JSON (URL) or blob (direct download)
  const rawContentType = response.headers['content-type'];
  const contentType = typeof rawContentType === 'string' ? rawContentType : '';

  if (contentType.includes('application/json')) {
    // Response is JSON with download URL
    const text = await (response.data as Blob).text();
    const json = JSON.parse(text);
    return json.data?.downloadUrl || null;
  } else {
    // Response is direct file download
    const blob = response.data;
    const contentDisposition = String(response.headers['content-disposition'] ?? '');
    const filenameMatch = /filename="(.+)"/.exec(contentDisposition);
    const filename = filenameMatch ? filenameMatch[1] : `statistics-export-${new Date().toISOString()}.${fileExtension}`;

    // Create download link
    const url = globalThis.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    globalThis.URL.revokeObjectURL(url);

    return null; // Already downloaded
  }
};

/**
 * Export statistics overview to Excel
 * Returns either a download URL or triggers direct download
 */
export const exportStatisticsToExcel = async (options?: {
  year?: number;
  fromDate?: string;
  toDate?: string;
  licenceFilter?: LicenceFilterType;
  statusFilter?: CompanyStatusFilter;
  companyId?: number;
}): Promise<string | null> =>
  exportStatisticsToFile('/statistics/export/excel', 'xlsx', options);

/**
 * Export company statistics to Excel
 */
export const exportCompanyStatisticsToExcel = async (
  companyId: number,
  options?: {
    year?: number;
    fromDate?: string;
    toDate?: string;
  }
): Promise<string> => {
  const params: any = {};
  if (options?.year) params.year = options.year;
  if (options?.fromDate) params.fromDate = options.fromDate;
  if (options?.toDate) params.toDate = options.toDate;

  const response = await api.get(`/statistics/company/${companyId}/export/excel`, { params });
  return response.data.data.downloadUrl;
};

/**
 * Export statistics overview to PDF
 * Returns either a download URL or triggers direct download
 */
export const exportStatisticsToPdf = async (options?: {
  year?: number;
  fromDate?: string;
  toDate?: string;
  licenceFilter?: LicenceFilterType;
  statusFilter?: CompanyStatusFilter;
  companyId?: number;
}): Promise<string | null> =>
  exportStatisticsToFile('/statistics/export/pdf', 'pdf', options);
